/**
 * Label de la columna "Stock" del presupuesto.
 *
 * Regla de negocio (paridad con el storefront de Magento):
 *
 *   stock > 0 y qty <= stock                → "Stock disponible"            (verde)
 *   stock >= 0 y qty > stock  + leadtime>0  → "N disponibles / + Stock en L días" (ámbar)
 *   stock > 0  y qty > stock  + leadtime<=0 → "N disponibles / Stock limitado"    (ámbar)
 *   stock === 0 y qty > stock + leadtime<=0 → "Sin stock"                   (rojo)
 *   stock < 0  (cualquier leadtime)         → "Consultar stock"             (rojo)
 *
 * Ojo: la API devuelve `stock` y `leadtime` como strings, así que hay que
 * normalizar a número antes de comparar — `if (leadtime)` con `"0"` da true y
 * es el bug que mostraba "+ Stock en 0 días" en vez de "Stock limitado".
 */

export const STOCK_COLORS = {
  available: "rgba(70, 140, 105, 1)",
  warning: "rgba(255, 197, 61, 1)",
  danger: "#f5222d",
} as const;

export type StockStatus =
  | "available" // stock suficiente
  | "restock" // falta stock pero se repone en L días
  | "limited" // falta stock y no hay reposición
  | "out" // stock 0 sin reposición
  | "check"; // stock negativo

export interface StockLabel {
  status: StockStatus;
  /** Una o dos líneas de copy, ya formateadas. */
  lines: string[];
  color: string;
}

/** `null`/`""`/no numérico → null, para poder distinguir "sin dato" de 0. */
function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export interface StockLabelInput {
  stock: unknown;
  leadtime: unknown;
  qty: unknown;
}

/** Devuelve `null` cuando el ítem no trae stock (el caller muestra "-"). */
export function getStockLabel(input: StockLabelInput): StockLabel | null {
  const stock = toNumber(input.stock);
  if (stock === null) return null;

  const leadtime = toNumber(input.leadtime) ?? 0;
  const qty = toNumber(input.qty) ?? 1;

  // Stock negativo: el dato no es confiable, hay que preguntarle al seller.
  if (stock < 0) {
    return {
      status: "check",
      lines: ["Consultar stock"],
      color: STOCK_COLORS.danger,
    };
  }

  if (stock > 0 && qty <= stock) {
    return {
      status: "available",
      lines: ["Stock disponible"],
      color: STOCK_COLORS.available,
    };
  }

  // Falta stock (qty > stock). Con reposición el faltante llega en L días.
  if (leadtime > 0) {
    return {
      status: "restock",
      lines: [`${stock} disponibles`, `+ Stock en ${leadtime} días`],
      color: STOCK_COLORS.warning,
    };
  }

  if (stock === 0) {
    return {
      status: "out",
      lines: ["Sin stock"],
      color: STOCK_COLORS.danger,
    };
  }

  return {
    status: "limited",
    lines: [`${stock} disponibles`, "Stock limitado"],
    color: STOCK_COLORS.warning,
  };
}
